'use client'
import { BulkEditOverlay } from '@/app/components/bulk-edit-overlay'
import Avatar from '@/components/avatar'
import FavoriteButton from '@/components/favorite-button'
import Username from '@/components/username'
import { useCheckFavorite } from '@/context/favorites-manager'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { canEdit } from '@/util/user-permissions'
import { GifOverlayProps } from '@giphy/react-components'
import { getContentOfGif, getCreatorOfGif, setGADataLayer } from 'analytics'
import { usePathname, useRouter } from 'next/navigation'
import { useContext, useRef } from 'react'
import TagOverlay from 'ui/src/components/overlay/tag-overlay'
import { useBulkEditOpen } from '../bulk-edit-state'
import ClipOverlay from './clip-overlay'
import ClipboardButton from './clipboard-button'
import EditButton from './edit-button'
import PrivacyOverlay from './privacy-overlay'

const HoverOverlayContainerTW: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="w-full h-full top-0 left-0 absolute">{children}</div>
)

const GPSTagOverlayTW: React.FC<{ tag: string }> = ({ tag }) => (
    <a
        className="bg-giphyBlack rounded-full h-9 leading-9 text-giphyGreen font-bold italic text-[15px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-[18px] max-w-full whitespace-nowrap text-ellipsis overflow-hidden"
        onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            location.href = '/edit/gifs?query=' + encodeURIComponent(tag)
        }}
    >
        #{tag}
    </a>
)

export const AttrOverlay = ({ gif, isHovered }: GifOverlayProps) => {
    const hasHovered = useRef(isHovered)
    const router = useRouter()
    if (isHovered) {
        // not rendering to avoid loading the avatar until hover
        hasHovered.current = true
    }
    const { user } = gif
    return user && hasHovered.current ? (
        <div
            onClick={(e) => {
                // stop the gif click event
                e.stopPropagation()
                setGADataLayer({
                    event: 'attribution_open_click',
                    options: {
                        content: getContentOfGif(gif),
                        creator: getCreatorOfGif(gif),
                    },
                })
                router.push(`/${user.username}`)
            }}
            className={cn('transition-opacity group ease-in', isHovered ? 'opacity-100' : 'opacity-0')}
        >
            <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-[75px] cursor-default"
                style={{
                    background: `linear-gradient(rgba(0, 0, 0, 0), rgba(18, 18, 18, 0.6))`,
                }}
            />
            <div className="absolute inset-x-2.5 bottom-2.5 flex gap-2">
                <Avatar user={user} size={32} />
                <Username
                    noLink
                    user={user}
                    className="text-giphyWhite [&_svg]:fill-giphyWhite text-[16px] font-semibold group-hover:underline"
                    alternateName={user.display_name}
                />
            </div>
        </div>
    ) : null
}

const TrendingIndicator = ({ colorClass }: { colorClass: string }) => (
    <div className={`absolute h-[14px] w-[14px] top-2 left-2 rounded-full ${colorClass}`} />
)

const GifOverlay = ({ isHovered, gif, width }: GifOverlayProps) => {
    const { user } = useContext(UserContext)
    const isFavorite = useCheckFavorite(isHovered ? (gif.id as string) : undefined)
    const pathname = usePathname()
    const bulkEditEnabled = useBulkEditOpen()
    const hasGpsTag = Array.isArray(gif.gps_tags) && gif.gps_tags.length > 0 && typeof gif.gps_tags[0] === 'string'

    let trendingIcon = null
    if (Array.isArray(gif.tags)) {
        if (gif.tags.includes('giphytrending')) {
            trendingIcon = <TrendingIndicator colorClass="bg-giphyGreen" />
        } else if (gif.tags.includes('giphytrendingqueue')) {
            trendingIcon = <TrendingIndicator colorClass="bg-blue-500" />
        } else if (gif.tags.includes('giphytrendingschedule')) {
            trendingIcon = <TrendingIndicator colorClass="bg-yellow-400" />
        }
    }

    return bulkEditEnabled ? (
        <>
            <PrivacyOverlay gif={gif} />
            <ClipOverlay gif={gif} />
            <BulkEditOverlay isHovered={isHovered} gif={gif} width={width} />
        </>
    ) : (
        <>
            {!isHovered && trendingIcon}
            {isHovered && hasGpsTag ? (
                <HoverOverlayContainerTW>
                    <div className="bg-giphyBlack/10 absolute right-2.5 top-2.5 flex items-center gap-3 rounded-md p-2">
                        {user && canEdit(user, gif) && <EditButton gif={gif} />}
                        <FavoriteButton
                            hideLabel
                            gif={gif}
                            className={cn(
                                'fill-giphyWhite size-5 opacity-5 duration-300 ease-in-out',
                                (isFavorite !== undefined || !user) && 'opacity-100'
                            )}
                            iconClassName="hover:scale-110 transition-all"
                            isFavorite={!!isFavorite}
                        />
                        <ClipboardButton gif={gif} />
                    </div>
                    {gif.gps_tags && <GPSTagOverlayTW tag={gif.gps_tags[0]} />}
                </HoverOverlayContainerTW>
            ) : (
                isHovered && (
                    <div className="bg-giphyBlack/10 absolute right-2.5 top-2.5 flex items-center gap-3 rounded-md p-2">
                        {user && canEdit(user, gif) && <EditButton gif={gif} />}
                        <FavoriteButton
                            hideLabel
                            gif={gif}
                            className={cn(
                                'fill-giphyWhite size-5 opacity-5 duration-300 ease-in-out',
                                (isFavorite !== undefined || !user) && 'opacity-100'
                            )}
                            iconClassName="hover:scale-110 transition-all"
                            isFavorite={!!isFavorite}
                        />
                        <ClipboardButton gif={gif} />
                    </div>
                )
            )}
            {gif.user && pathname.indexOf(`/${gif.user.username}`) !== 0 ? (
                <AttrOverlay gif={gif} width={width} isHovered={isHovered} />
            ) : (
                <TagOverlay gif={gif} isHovered={isHovered} width={width} />
            )}
            <PrivacyOverlay gif={gif} />
        </>
    )
}
export default GifOverlay
